import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AppLaunchResult } from '@office-start/launcher-types';
import { logUserAction } from '@office-start/telemetry-service';
import { Area } from '@office-start/telemetry-types';
import { getReferral } from '@office-start/telemetry-utility';
import { Subscription } from 'rxjs';

import { LinkTarget } from '../shared/constants/link-target.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { Workload } from '../shared/models/workload.model';
import { AppLauncherService } from '../shared/services/app-launcher.service';
import { EventService } from '../shared/services/event.service';
import { ShellService } from '../shared/services/shell.service';
import { ThemingService } from '../shared/services/theming.service';
import { LaunchAction } from '../shared/services/url-launcher.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UrlParser } from '../shared/utilities/url-parser.utility';
import { UnpinWorkloadEvent } from './app-tile-state-event.model';
import { AppTilesConfigService } from './shared/app-tiles-config.service';

// Only to be used in mobile views
@Component({
    selector: 'ohp-app-tile',
    templateUrl: './app-tile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppTileComponent implements OnInit, OnChanges, OnDestroy {

    @Output() public readonly unpinWorkload: EventEmitter<UnpinWorkloadEvent>;

    @Input() public workload: Readonly<Workload>;

    @Input() public inExpandedView: boolean;

    @Input() public focusOnLoad: boolean;

    @Input() public pinnedApp: boolean;

    @Input() public set forceWhiteBackground(value: boolean) {
        this._forceWhiteBackground = value;
        this.setBackgroundColor();
    }

    public get forceWhiteBackground() {
        return this._forceWhiteBackground;
    }

    public isProvisioned: boolean;
    public setWhiteBackground: boolean;

    public set showHover(value: boolean) {
        this._showHover = value;
    }

    public get showHover(): boolean {
        return this._showHover;
    }

    public provisioningText: string;
    public ariaLabel: string;
    public elementId: string;
    public thirdPartyIconAriaLabel: string;
    public preloadPath: string | OfficeRoute;
    public appColor: string;
    public iconLoaded: boolean;
    public removeAppAriaLabel: string;

    private _showHover: boolean;
    private _forceWhiteBackground: boolean;
    private themeShadesSubscription: Subscription;
    private checkedODSPProvisioningStatus: boolean;

    constructor(
        private readonly appTilesConfigService: AppTilesConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly workloadsService: WorkloadsService,
        private readonly appLauncherService: AppLauncherService,
        private readonly locService: LocService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly themingService: ThemingService,
        private readonly shellService: ShellService
    ) {
        this.unpinWorkload = new EventEmitter<UnpinWorkloadEvent>();
    }

    public ngOnInit() {
        const config = this.appTilesConfigService.config;
        this.isProvisioned = !!this.workload.Url;
        this.ariaLabel = this.workload.Text;
        this.thirdPartyIconAriaLabel = this.locService.format(this.locService.ThirdPartyIconAriaLabel, this.workload.Text);
        this.preloadPath = this.workload.InAppNav ? OfficeRoute.Start : UrlParser.getOrigin(this.workload.Url);
        this.removeAppAriaLabel = this.locService.RemoveDocumentActionTitle;

        if (!this.isProvisioned) {
            this.provisioningText = config.resources && config.resources.ProvisioningTileText;
            return;
        }
        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.themeShadesSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                const isDarkMode = event;
                if (isDarkMode && this.workload.ChangeColorInDarkMode) {
                        this.appColor = 'white';
                    } else {
                        this.appColor = '';
                    }
                });
        }
        this.setBackgroundColor();
    }

    public ngOnChanges(changesObj: SimpleChanges) {
        if (changesObj.hasOwnProperty('workload')) {
            this.elementId = `${changesObj.workload.currentValue.Id}_link`;
            this.setBackgroundColor();
            this.iconLoaded = this.iconLoaded && changesObj.workload.currentValue.ThirdPartyIcon === changesObj.workload.previousValue.ThirdPartyIcon;
        }
    }

    public ngOnDestroy() {
        if (this.themeShadesSubscription) {
            this.themeShadesSubscription.unsubscribe();
        }
    }

    public mouseOverTile() {
        if (this.workload.Url) {
            this.showHover = true;
        }

        if (!this.checkedODSPProvisioningStatus && (this.workload.FileType === FileType.SharePoint || this.workload.FileType === FileType.OneDrive)) {
            this.shellService.initializeProvisioningStatus();
            this.checkedODSPProvisioningStatus = true;
        }
    }

    public onTileClick(event: Event) {
        this.showHover = false;

        // Make clicking on provisioning tiles do nothing.
        // Don't log a click event.
        if (!this.workload.Url) {
            if (event) {
                event.preventDefault();
                return;
            }
        }

        // Only wait to log click if we open in a new tab, otherwise we risk losing the event
        const logAfterResult = this.contextService.sharedContext.defaultLinkTarget === LinkTarget.NewTab;
        if (!logAfterResult) {
            this.instrumentTileClick('Web');
        }

        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
            import('@office-start/launcher-service').then(
                launchSvc => {
                    launchSvc.launchApp({ action: LaunchAction.OpenAppTile, event, appName: this.workload.Text, url: this.workload.Url})
                    .then(result => {
                        if (logAfterResult) {
                            this.instrumentTileClick(result.result);
                        }
                    });
                }
            );
        } else {
            this.appLauncherService.launchApp(this.workload, event, LaunchAction.OpenAppTile)
            .then(result => {
                if (logAfterResult) {
                    this.instrumentTileClick(result);
                }
            });
        }
    }

    public unfocusTile() {
        this.showHover = false;
    }

    public instrumentTileClick(launchResult?: AppLaunchResult) {
        const referral = launchResult ? this.workload.Referral : '';
        const safeReferral = getReferral(this.workload.Referral);
        const componentArea = KnownAreas.AppTiles;

        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = this.inExpandedView ? 'expanded' : 'reduced';
        params[InstrumentationParam.ReferralType] = this.workloadsService.getReferralType(this.workload).toString();

        this.instrumentationService
            .logClickEvent(KnownInstrumentationEvents.GenericClick, safeReferral, componentArea, referral, launchResult === 'Native' ? KnownReferralPlatform.Native : KnownReferralPlatform.Web, params, [], DataSecurityLevel.Security, this.workload.InAppNav);
        logUserAction({
            id: safeReferral,
            area: componentArea as Area,
            result: referral ? 'Refer' : 'Navigate',
            target: referral ? safeReferral : 'Other'
        });
        this.eventService.broadcastEvent({
            name: AngularEvents.appTileClick,
            args: [this.workload.AppId]
        });
    }

    public instrumentRightClick() {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.ElementId] = getReferral(this.workload.Referral);
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.AppTileRightClick, params);
    }

    public onIconLoad() {
        // Wait a tick to avoid a one-time devMode
        // unidirectional-data-flow-violation error
        setTimeout(() => {
            this.iconLoaded = true;
            this.changeDetectorRef.markForCheck();
        });
    }

    public onIconError() {
        // Wait a tick to avoid a one-time devMode
        // unidirectional-data-flow-violation error
        setTimeout(() => {
            this.iconLoaded = false;
            this.changeDetectorRef.markForCheck();
        });
    }

    public async unpinApp(event: Event) {
        if (event) {
            event.preventDefault();
        }

        this.unpinWorkload.emit({ item: this.workload });
    }

    private setBackgroundColor(): void {
        this.setWhiteBackground = this.forceWhiteBackground;
    }
}
